import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  getFieldValue,
  getMultilistDescriptions,
  getReferenceDisplayName,
  getSitePhoneNumber,
  parseOperatingHours,
} from './utils';
import { SiteMasterRouteFields, SitecoreReferenceItem, SiteRecruitingStudy } from './types';

/** Static trial design values applied to every active study (business decision). */
export const DEFAULT_TRIAL_DESIGN: string[] = [
  'https://schema.org/RandomizedTrial',
  'https://schema.org/DoubleBlindedTrial',
];

interface JsonLdPostalAddress {
  '@type': 'PostalAddress';
  streetAddress?: string;
  addressLocality?: string;
  addressRegion?: string;
  postalCode?: string;
  addressCountry?: string;
}

interface JsonLdGeoCoordinates {
  '@type': 'GeoCoordinates';
  latitude: number;
  longitude: number;
}

interface JsonLdAdministrativeArea {
  '@type': 'AdministrativeArea';
  name: string;
}

interface JsonLdOpeningHoursSpecification {
  '@type': 'OpeningHoursSpecification';
  dayOfWeek: string[];
  opens: string;
  closes: string;
}

interface JsonLdMedicalTrial {
  '@type': 'MedicalTrial';
  name?: string;
  description?: string;
  url?: string;
  status: string;
  identifier?: {
    '@type': 'PropertyValue';
    propertyID: string;
    value: string;
  };
  sponsor?: {
    '@type': 'Organization';
    name: string;
  };
  trialDesign: string[];
  healthCondition?: Array<{
    '@type': 'MedicalCondition';
    name: string;
  }>;
}

export interface SiteDetailJsonLd {
  '@context': 'https://schema.org';
  '@type': 'MedicalClinic';
  name?: string;
  description?: string;
  url?: string;
  image?: string[];
  address?: JsonLdPostalAddress;
  geo?: JsonLdGeoCoordinates;
  telephone?: string;
  email: string;
  medicalSpecialty?: string[];
  areaServed?: JsonLdAdministrativeArea[];
  openingHoursSpecification?: JsonLdOpeningHoursSpecification[];
  subjectOf?: JsonLdMedicalTrial[];
}

export type SiteSettingsLike =
  | {
      siteNumber?: Field<string>;
      baseUrl?: Field<string>;
    }
  | null
  | undefined;

/**
 * Build an absolute URL on the site origin.
 * Relative paths and absolute URLs on other hosts are rewritten to `baseUrl` + path.
 */
const toSiteAbsoluteUrl = (url: string, baseUrl?: string): string | undefined => {
  const trimmedBase = baseUrl?.trim().replace(/\/$/, '') || '';
  const trimmedUrl = url.trim();
  if (!trimmedBase || !trimmedUrl) {
    return undefined;
  }

  let path: string;
  try {
    if (/^https?:\/\//i.test(trimmedUrl)) {
      const parsed = new URL(trimmedUrl);
      path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } else {
      path = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
    }
  } catch {
    return undefined;
  }

  return `${trimmedBase}${path}`.replace(/([^:]\/)\/+/g, '$1');
};

/** Blank-safe coordinate parsing: '' -> null, never 0 for missing values. */
const parseCoordinateInput = (value?: string): number | null => {
  if (value === undefined || value === null) {
    return null;
  }
  const raw = value.trim();
  if (raw === '') {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const isValidLatitude = (value: number): boolean => value >= -90 && value <= 90;
const isValidLongitude = (value: number): boolean => value >= -180 && value <= 180;

/**
 * State code for areaServed display ("City, ST").
 * Prefers KeyValue `abbreviation` (e.g. MD); falls back to display name.
 */
const getStateCode = (siteState?: SitecoreReferenceItem | Field<string>): string => {
  if (siteState && 'fields' in siteState) {
    const abbreviation = getFieldValue(siteState.fields?.abbreviation);
    if (abbreviation) {
      return abbreviation;
    }
  }
  return getReferenceDisplayName(siteState);
};

/**
 * ISO country code for PostalAddress.addressCountry (e.g. "US").
 * Prefers KeyValue `countryCode`; falls back to displayName/name.
 */
const getCountryCode = (siteCountry?: SitecoreReferenceItem | Field<string>): string => {
  if (siteCountry && 'fields' in siteCountry) {
    const countryCode = getFieldValue(siteCountry.fields?.countryCode);
    if (countryCode) {
      return countryCode;
    }
  }
  if (siteCountry && 'displayName' in siteCountry && siteCountry.displayName) {
    return siteCountry.displayName;
  }
  if (
    siteCountry &&
    'name' in siteCountry &&
    typeof siteCountry.name === 'string' &&
    siteCountry.name
  ) {
    return siteCountry.name;
  }
  return getReferenceDisplayName(siteCountry);
};

/** RichText -> plain text for schema description. */
const stripRichText = (html?: string): string => {
  if (!html) {
    return '';
  }
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * First sentence only for MedicalClinic / MedicalTrial description.
 * Splits on .!? followed by whitespace + capital letter (or end of string).
 */
const toFirstSentence = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) {
    return '';
  }
  const match = trimmed.match(/^(.+?[.!?])(?=\s+[A-Z]|$)/);
  return match ? match[1].trim() : trimmed;
};

const DAY_NAME_MAP: Record<string, string> = {
  mon: 'Monday',
  monday: 'Monday',
  tue: 'Tuesday',
  tues: 'Tuesday',
  tuesday: 'Tuesday',
  wed: 'Wednesday',
  wednesday: 'Wednesday',
  thu: 'Thursday',
  thur: 'Thursday',
  thurs: 'Thursday',
  thursday: 'Thursday',
  fri: 'Friday',
  friday: 'Friday',
  sat: 'Saturday',
  saturday: 'Saturday',
  sun: 'Sunday',
  sunday: 'Sunday',
};

/** '8:00 AM' -> '08:00', '6:00 PM' -> '18:00'; null when unparseable. */
const to24Hour = (value?: string): string | null => {
  const match = value?.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) {
    return null;
  }
  let hours = Number(match[1]);
  const minutes = match[2] ?? '00';
  const meridiem = match[3]?.toUpperCase();
  if (meridiem === 'PM' && hours < 12) {
    hours += 12;
  }
  if (meridiem === 'AM' && hours === 12) {
    hours = 0;
  }
  if (hours > 23 || Number(minutes) > 59) {
    return null;
  }
  return `${String(hours).padStart(2, '0')}:${minutes}`;
};

/** Split "8:00 AM - 5:00 PM" / en-dash / "8:00 AM to 5:00 PM" into open and close segments. */
const splitHoursRange = (hours: string): { opens: string; closes: string } | null => {
  const trimmed = hours.trim();
  if (!trimmed || trimmed.toLowerCase() === 'closed') {
    return null;
  }
  const withAsciiDash = trimmed.replace(/[\u2013\u2014]/g, '-');
  const toMatch = withAsciiDash.match(/^(.+?)\s+to\s+(.+)$/i);
  if (toMatch) {
    return { opens: toMatch[1].trim(), closes: toMatch[2].trim() };
  }
  const dashIndex = withAsciiDash.indexOf('-');
  if (dashIndex === -1) {
    return null;
  }
  const opens = withAsciiDash.slice(0, dashIndex).trim();
  const closes = withAsciiDash.slice(dashIndex + 1).trim();
  if (!opens || !closes) {
    return null;
  }
  return { opens, closes };
};

const buildOpeningHoursSpecification = (raw?: string): JsonLdOpeningHoursSpecification[] => {
  const specifications: JsonLdOpeningHoursSpecification[] = [];

  parseOperatingHours(raw).forEach((entry) => {
    const day = DAY_NAME_MAP[entry.day.trim().toLowerCase()];
    const range = splitHoursRange(entry.hours);
    const opens = range ? to24Hour(range.opens) : null;
    const closes = range ? to24Hour(range.closes) : null;
    if (!day || !opens || !closes) {
      return;
    }
    specifications.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [day],
      opens,
      closes,
    });
  });

  return specifications;
};

const buildStudyJsonLd = (study: SiteRecruitingStudy, siteBaseUrl?: string): JsonLdMedicalTrial => {
  const schema: JsonLdMedicalTrial = {
    '@type': 'MedicalTrial',
    status: 'https://schema.org/Recruiting',
    trialDesign: [...DEFAULT_TRIAL_DESIGN],
  };

  if (study.title) {
    schema.name = study.title;
  }
  const description = toFirstSentence(stripRichText(study.description));
  if (description) {
    schema.description = description;
  }
  const trialUrl = toSiteAbsoluteUrl(study.detailsUrl ?? '', siteBaseUrl);
  if (trialUrl) {
    schema.url = trialUrl;
  }
  if (study.nctId) {
    schema.identifier = {
      '@type': 'PropertyValue',
      propertyID: 'ClinicalTrials.gov',
      value: study.nctId,
    };
  }
  if (study.sponsor) {
    schema.sponsor = { '@type': 'Organization', name: study.sponsor };
  }

  const conditions = (study.conditionLabels ?? []).filter(Boolean);
  if (conditions.length) {
    schema.healthCondition = conditions.map((name) => ({
      '@type': 'MedicalCondition' as const,
      name,
    }));
  }

  return schema;
};

/**
 * Builds the Schema.org MedicalClinic JSON-LD object for a Site Detail page.
 * Blank-safe: properties with no source data are omitted (except `logo` and
 * the `email` placeholder, which are always emitted).
 */
export function buildSiteDetailJsonLd(
  routeFields: SiteMasterRouteFields,
  siteSettings: SiteSettingsLike,
  studies?: SiteRecruitingStudy[],
  pageUrl?: string
): SiteDetailJsonLd {
  const schema: SiteDetailJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    email: '',
  };

  const name = getFieldValue(routeFields.siteName);
  if (name) {
    schema.name = name;
  }

  const description = toFirstSentence(stripRichText(routeFields.siteDescription?.value));
  if (description) {
    schema.description = description;
  }

  if (pageUrl) {
    schema.url = pageUrl;
  }

  const images = [routeFields.siteImage, routeFields.siteImageDesktop]
    .map((image) => image?.value?.src)
    .filter((src): src is string => Boolean(src));
  if (images.length) {
    schema.image = images;
  }

  const streetAddress = getFieldValue(routeFields.siteAddress);
  const addressLocality = getFieldValue(routeFields.siteCity);
  const addressRegion = getReferenceDisplayName(routeFields.siteState);
  const postalCode = getFieldValue(routeFields.siteZip);
  const addressCountry = getCountryCode(routeFields.siteCountry);
  const address: JsonLdPostalAddress = { '@type': 'PostalAddress' };
  if (streetAddress) {
    address.streetAddress = streetAddress;
  }
  if (addressLocality) {
    address.addressLocality = addressLocality;
  }
  if (addressRegion) {
    address.addressRegion = addressRegion;
  }
  if (postalCode) {
    address.postalCode = postalCode;
  }
  if (addressCountry) {
    address.addressCountry = addressCountry;
  }
  if (Object.keys(address).length > 1) {
    schema.address = address;
  }

  const latitude = parseCoordinateInput(getFieldValue(routeFields.siteLatitude));
  const longitude = parseCoordinateInput(getFieldValue(routeFields.siteLongitude));
  if (
    latitude !== null &&
    longitude !== null &&
    (latitude !== 0 || longitude !== 0) &&
    isValidLatitude(latitude) &&
    isValidLongitude(longitude)
  ) {
    schema.geo = { '@type': 'GeoCoordinates', latitude, longitude };
  }

  const telephone = getSitePhoneNumber(routeFields, siteSettings);
  if (telephone) {
    schema.telephone = telephone;
  }
  //will be removing this soon -- preview purpose only
  // Communities/conditions store the display label on `fields.description` (KeyValue).
  const specialties = getMultilistDescriptions(routeFields.siteAssociatedCommunities);
  if (specialties.length) {
    schema.medicalSpecialty = specialties;
  }

  const stateCode = getStateCode(routeFields.siteState);
  const areaServedName = [addressLocality, stateCode].filter(Boolean).join(', ');
  if (areaServedName) {
    schema.areaServed = [{ '@type': 'AdministrativeArea', name: areaServedName }];
  }

  const openingHours = buildOpeningHoursSpecification(
    getFieldValue(routeFields.siteOperatingHours)
  );
  if (openingHours.length) {
    schema.openingHoursSpecification = openingHours;
  }

  const siteBaseUrl = getFieldValue(siteSettings?.baseUrl);
  const activeStudies = (studies ?? []).map((study) => buildStudyJsonLd(study, siteBaseUrl));
  if (activeStudies.length) {
    schema.subjectOf = activeStudies;
  }

  return schema;
}
